<template>
  <div class="relative">
    <!-- Notification Bell -->
    <button 
      @click="toggleNotifications"
      class="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
    >
      <Bell class="h-5 w-5" />
      <span 
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <!-- Notification Dropdown -->
    <div 
      v-if="isOpen"
      class="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50"
    >
      <div class="p-4 border-b">
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-semibold">Notifications</h3>
          <button 
            @click="markAllAsRead"
            class="text-sm text-blue-600 hover:text-blue-800"
            v-if="unreadCount > 0"
          >
            Mark all as read
          </button>
        </div>
      </div>

      <div class="max-h-96 overflow-y-auto">
        <div v-if="notifications.length === 0" class="p-4 text-center text-gray-500">
          No notifications
        </div>
        
        <div v-else>
          <div 
            v-for="notification in notifications" 
            :key="notification.id"
            @click="handleNotificationClick(notification)"
            :class="[
              'p-4 border-b cursor-pointer hover:bg-gray-50',
              !notification.read ? 'bg-blue-50' : ''
            ]"
          >
            <div class="flex items-start gap-3">
              <div 
                :class="[
                  'h-2 w-2 rounded-full mt-2 flex-shrink-0',
                  getNotificationColor(notification.type)
                ]"
              ></div>
              <div class="flex-1 min-w-0">
                <div class="flex justify-between items-start">
                  <h4 class="text-sm font-medium text-gray-900 truncate">
                    {{ notification.title }}
                  </h4>
                  <span class="text-xs text-gray-500 ml-2">
                    {{ formatTime(notification.createdAt) }}
                  </span>
                </div>
                <p class="text-sm text-gray-600 mt-1">{{ notification.message }}</p>
                <div v-if="notification.action" class="mt-2">
                  <span class="text-xs text-blue-600">{{ notification.action }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="p-4 border-t">
        <button 
          @click="viewAllNotifications"
          class="w-full text-center text-sm text-blue-600 hover:text-blue-800"
        >
          View all notifications
        </button>
      </div>
    </div>

    <!-- Backdrop -->
    <div 
      v-if="isOpen"
      @click="closeNotifications"
      class="fixed inset-0 z-40"
    ></div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Bell } from 'lucide-vue-next'

export default {
  name: 'NotificationCenter',
  components: {
    Bell
  },
  setup() {
    const isOpen = ref(false)
    const notifications = ref([])

    const unreadCount = computed(() => {
      return notifications.value.filter(n => !n.read).length
    })

    const toggleNotifications = () => {
      isOpen.value = !isOpen.value
    }

    const closeNotifications = () => {
      isOpen.value = false
    }

    const markAllAsRead = () => {
      notifications.value.forEach(notification => {
        notification.read = true
      })
    }

    const handleNotificationClick = (notification) => {
      if (!notification.read) {
        notification.read = true
      }
      
      // Handle notification action
      if (notification.actionUrl) {
        // Navigate to action URL
        window.location.href = notification.actionUrl
      }
    }

    const viewAllNotifications = () => {
      // Navigate to full notifications page
      console.log('Navigate to all notifications')
    }

    const getNotificationColor = (type) => {
      switch (type) {
        case 'success': return 'bg-green-500'
        case 'warning': return 'bg-yellow-500'
        case 'error': return 'bg-red-500'
        case 'info': return 'bg-blue-500'
        default: return 'bg-gray-500'
      }
    }

    const formatTime = (dateString) => {
      const date = new Date(dateString)
      const now = new Date()
      const diffInMinutes = Math.floor((now - date) / (1000 * 60))
      
      if (diffInMinutes < 1) return 'Just now'
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
      return date.toLocaleDateString()
    }

    // Mock data for demonstration
    const loadMockNotifications = () => {
      notifications.value = [
        {
          id: 1,
          title: 'Time Entry Reminder',
          message: 'You forgot to clock out yesterday. Please update your time entry.',
          type: 'warning',
          read: false,
          createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          action: 'Update Time Entry',
          actionUrl: '/dashboard'
        },
        {
          id: 2,
          title: 'Leave Request Approved',
          message: 'Your vacation request for Dec 15-20 has been approved.',
          type: 'success',
          read: false,
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          action: 'View Details',
          actionUrl: '/leave-requests'
        },
        {
          id: 3,
          title: 'Timesheet Review',
          message: 'Your timesheet for last week needs manager approval.',
          type: 'info',
          read: true,
          createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          action: 'Review Timesheet',
          actionUrl: '/timesheet'
        },
        {
          id: 4,
          title: 'Overtime Alert',
          message: 'You have exceeded 40 hours this week. Overtime pay will apply.',
          type: 'warning',
          read: true,
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          action: 'View Hours',
          actionUrl: '/dashboard'
        }
      ]
    }

    onMounted(() => {
      loadMockNotifications()
    })

    return {
      isOpen,
      notifications,
      unreadCount,
      toggleNotifications,
      closeNotifications,
      markAllAsRead,
      handleNotificationClick,
      viewAllNotifications,
      getNotificationColor,
      formatTime
    }
  }
}
</script>

